import { zodResolver } from "@hookform/resolvers/zod";
import { Box, IconButton, Stack } from "@mui/material";
import { usePayAccountWarmUp } from "Components/Customer/Payment/PaymentForms/usePayAccountWarmUp";
import { BankAccountDropdown } from "Components/Shared/Inputs/Form/BankAccountDropdown";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import type { BankAccountListItem } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { SwapVert } from "@mui/icons-material";

const RESOURCES = Resources.Transfer;

const transferSchema = z.object({
  fromAccountPublicID: z.string().min(1),
  toAccountPublicID: z.string().min(1),
  amount: z.number().positive(),
  note: z.string().optional(),
});

export type TransferFormData = z.infer<typeof transferSchema>;

type Props = {
  bankAccounts: BankAccountListItem[];
  defaultValues: Partial<TransferFormData>;
  onSubmit: (data: TransferFormData) => void;
  isSubmitting?: boolean;
  isDisabled?: boolean;
};

export const TransferForm = ({
  bankAccounts,
  defaultValues,
  onSubmit,
  isSubmitting,
  isDisabled,
}: Props) => {
  const { t } = useResource();

  const { handleSubmit, control, watch, setValue } = useForm<TransferFormData>({
    resolver: zodResolver(transferSchema),
    defaultValues,
  });

  const fromPublicID = watch("fromAccountPublicID");
  const toPublicID = watch("toAccountPublicID");
  // The transfer is paid from the source account — warm its bank token like the payment forms do.
  const { onBankAccountChange: warmUpSourceAccount } = usePayAccountWarmUp(
    defaultValues.fromAccountPublicID,
  );

  const toAccounts = bankAccounts.filter(
    (account) => account.publicID !== fromPublicID,
  );

  const handleFromChange = (newFromPublicID: string) => {
    warmUpSourceAccount(newFromPublicID);
    if (toPublicID === newFromPublicID) {
      const firstAvailable = bankAccounts.find(
        (a) => a.publicID !== newFromPublicID,
      );
      if (firstAvailable) {
        setValue("toAccountPublicID", firstAvailable.publicID);
      }
    }
  };

  const handleSwap = () => {
    warmUpSourceAccount(toPublicID);
    setValue("fromAccountPublicID", toPublicID);
    setValue("toAccountPublicID", fromPublicID);
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={3}>
      <BankAccountDropdown
        control={control}
        name="fromAccountPublicID"
        label={t(RESOURCES.FromAccount)}
        accounts={bankAccounts}
        disabled={isDisabled}
        isBalanceVisible
        onValueChange={handleFromChange}
      />

      <Box display="flex" justifyContent="center">
        <IconButton
          onClick={handleSwap}
          size="small"
          disabled={isDisabled}
          sx={{ mb: -6 }}
        >
          <SwapVert />
        </IconButton>
      </Box>

      <BankAccountDropdown
        control={control}
        name="toAccountPublicID"
        label={t(RESOURCES.ToAccount)}
        accounts={toAccounts}
        disabled={isDisabled}
        isBalanceVisible
      />

      <FormInput
        control={control}
        name="amount"
        label={t(RESOURCES.Amount)}
        type="number"
        disabled={isDisabled}
      />

      <FormInput
        control={control}
        name="note"
        label={t(RESOURCES.Note)}
        disabled={isDisabled}
      />

      <PrimaryButton
        fullWidth
        type="submit"
        color="primary"
        disabled={isDisabled || isSubmitting}
        isLoading={isSubmitting}
      >
        {t(RESOURCES.SubmitButton)}
      </PrimaryButton>
    </Stack>
  );
};
